import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  IconButton,
  Checkbox,
  Skeleton,
  Alert,
  OutlinedInput,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  InfoOutlined,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import AppGridContainer from '@tenant/core/AppGridContainer';
import { 
  useGetAppPermissions, 
  useUpdateAppPermission,
  useGetNextAvailablePermissionId,
  useCheckPermissionIdExists,
  useAddAppPermission,
  useDeleteAppPermission,
  useSyncAppPermission,
  useGetAppPortalPermissions,
  useUpdateAppPortalPermission,
  useGetNextAvailablePortalPermissionId,
  useCheckPortalPermissionIdExists,
  useAddAppPortalPermission,
  useDeleteAppPortalPermission,
  useSyncAppPortalPermission
} from 'pages/listApp/hooks/useAppPermissions';
import { 
  transformPermissionData, 
  getRoleOptions, 
  getRoleNamesFromIds,
  RoleTypeNames,
  PortalRoleTypeNames,
  getRoleTypeNames
} from 'helpers/permissionHelpers';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

const PermissionConfigurationStep = ({
  values = {},
  errors = {},
  touched = {},
  setFieldValue = () => {},
}) => {
  const [editingRowIndex, setEditingRowIndex] = useState(-1);
  const [editingPermission, setEditingPermission] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [overwriteAll, setOverwriteAll] = useState(false);
  const [activeTab, setActiveTab] = useState('BackOffice');
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);
  const [pendingSyncData, setPendingSyncData] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPermission, setNewPermission] = useState({
    id: '',
    permissionName: '',
    isParent: false,
    parentPermission: '',
    defaultRole: '',
    defaultRoleIds: [],
    sync: 'O',
  });

  // Conditional hooks based on active tab
  const isPortalTab = activeTab === 'CustomerPortal';
  
  // Get appropriate role type names based on active tab
  const currentRoleTypeNames = getRoleTypeNames(isPortalTab);
  const currentRoleOptions = getRoleOptions(isPortalTab);
  
  // Fetch permissions from API - Back Office
  const { 
    data: backOfficePermissions, 
    isLoading: isLoadingBackOffice, 
    error: backOfficeError,
    refetch: refetchBackOffice
  } = useGetAppPermissions(values.AppId);

  // Fetch permissions from API - Portal
  const { 
    data: portalPermissions, 
    isLoading: isLoadingPortal, 
    error: portalError,
    refetch: refetchPortal
  } = useGetAppPortalPermissions(values.AppId);

  // Use appropriate data based on active tab
  const apiPermissions = isPortalTab ? portalPermissions : backOfficePermissions;
  const isLoading = isPortalTab ? isLoadingPortal : isLoadingBackOffice;
  const apiError = isPortalTab ? portalError : backOfficeError;
  const refetch = isPortalTab ? refetchPortal : refetchBackOffice;

  // Fetch next available permission ID - Back Office
  const { 
    data: backOfficeNextIdData, 
    refetch: fetchBackOfficeNextId 
  } = useGetNextAvailablePermissionId();

  // Fetch next available permission ID - Portal
  const { 
    data: portalNextIdData, 
    refetch: fetchPortalNextId 
  } = useGetNextAvailablePortalPermissionId();

  const nextIdData = isPortalTab ? portalNextIdData : backOfficeNextIdData;
  const fetchNextId = isPortalTab ? fetchPortalNextId : fetchBackOfficeNextId;

  // Check permission ID exists - Back Office
  const { 
    mutate: checkBackOfficeIdExists, 
    data: backOfficeIdExistsData, 
    isPending: isCheckingBackOfficeId 
  } = useCheckPermissionIdExists();

  // Check permission ID exists - Portal
  const { 
    mutate: checkPortalIdExists, 
    data: portalIdExistsData, 
    isPending: isCheckingPortalId 
  } = useCheckPortalPermissionIdExists();

  const checkIdExists = isPortalTab ? checkPortalIdExists : checkBackOfficeIdExists;
  const idExistsData = isPortalTab ? portalIdExistsData : backOfficeIdExistsData;
  const isCheckingId = isPortalTab ? isCheckingPortalId : isCheckingBackOfficeId;

  // Sync permission mutation - Back Office
  const { 
    mutate: syncBackOfficePermission, 
    isPending: isSyncingBackOfficePermission 
  } = useSyncAppPermission();

  // Sync permission mutation - Portal
  const { 
    mutate: syncPortalPermission, 
    isPending: isSyncingPortalPermission 
  } = useSyncAppPortalPermission();

  const syncPermission = isPortalTab ? syncPortalPermission : syncBackOfficePermission;
  const isSyncingPermission = isPortalTab ? isSyncingPortalPermission : isSyncingBackOfficePermission;

  // Delete permission mutation - Back Office
  const { 
    mutate: deleteBackOfficePermission, 
    isPending: isDeletingBackOfficePermission 
  } = useDeleteAppPermission();

  // Delete permission mutation - Portal
  const { 
    mutate: deletePortalPermission, 
    isPending: isDeletingPortalPermission 
  } = useDeleteAppPortalPermission();

  const deletePermission = isPortalTab ? deletePortalPermission : deleteBackOfficePermission;
  const isDeletingPermission = isPortalTab ? isDeletingPortalPermission : isDeletingBackOfficePermission;

  // Add permission mutation - Back Office
  const { 
    mutate: addBackOfficePermission, 
    isPending: isAddingBackOfficePermission 
  } = useAddAppPermission();

  // Add permission mutation - Portal
  const { 
    mutate: addPortalPermission, 
    isPending: isAddingPortalPermission 
  } = useAddAppPortalPermission();

  const addPermission = isPortalTab ? addPortalPermission : addBackOfficePermission;
  const isAddingPermission = isPortalTab ? isAddingPortalPermission : isAddingBackOfficePermission;

  // Update permission mutation - Back Office
  const { 
    mutate: updateBackOfficePermission, 
    isPending: isUpdatingBackOffice 
  } = useUpdateAppPermission();

  // Update permission mutation - Portal
  const { 
    mutate: updatePortalPermission, 
    isPending: isUpdatingPortal 
  } = useUpdateAppPortalPermission();

  const updatePermission = isPortalTab ? updatePortalPermission : updateBackOfficePermission;
  const isUpdating = isPortalTab ? isUpdatingPortal : isUpdatingBackOffice;

  const [permissions, setPermissions] = useState([]);

  // Update permissions when API data loads
  useEffect(() => {
    if (apiPermissions && Array.isArray(apiPermissions)) {
      const transformedData = transformPermissionData(apiPermissions);
      setPermissions(transformedData);
      setFieldValue('permissions', transformedData);
    }
  }, [apiPermissions, setFieldValue]);

  // Get role options (this will be dynamic based on tab)
  const roleOptions = currentRoleOptions;

  // Get parent permissions for dropdown
  const parentPermissions = permissions.filter(permission => permission.isParent);

  const handleAddPermission = async () => {
    try {
      // Fetch next available ID
      const nextIdResponse = await fetchNextId();
      const nextId = nextIdResponse?.data?.NextAvailablePermissionId || '';
      
      setIsAddingNew(true);
      setNewPermission({
        id: nextId.toString(),
        permissionName: '',
        isParent: false,
        parentPermission: '',
        defaultRole: '',
        defaultRoleIds: [],
        sync: 'O',
      });
    } catch (error) {
      console.error('Error fetching next available ID:', error);
      // Fallback to empty ID if API fails
      setIsAddingNew(true);
      setNewPermission({
        id: '',
        permissionName: '',
        isParent: false,
        parentPermission: '',
        defaultRole: '',
        defaultRoleIds: [],
        sync: 'O',
      });
    }
  };

  const handleEditPermission = (index) => {
    if (editingRowIndex === index) {
      // If already editing this row, cancel edit
      setEditingRowIndex(-1);
      setEditingPermission(null);
    } else {
      // Start editing this row
      setEditingRowIndex(index);
      setEditingPermission({ ...permissions[index] });
    }
  };

  const handleSaveRowEdit = (index) => {
    if (!editingPermission?.permissionName?.trim()) return;
    
    // Find parent permission ID if parent permission is selected
    const parentPermissionId = editingPermission.parentPermission && editingPermission.parentPermission !== 'Dropdown' 
      ? permissions.find(p => p.permissionName === editingPermission.parentPermission)?.id 
      : null;

    const saveData = {
      AppId: values.AppId,
      PermissionId: parseInt(editingPermission.id),
      PermissionName: editingPermission.permissionName,
      ParentPermissionId: parentPermissionId ? parseInt(parentPermissionId) : null,
      DefaultRoleTypeIds: editingPermission.defaultRoleIds || []
    };

    setPendingSaveData({ saveData, index });
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    if (!pendingSaveData) return;

    updatePermission(pendingSaveData.saveData, {
      onSuccess: () => {
        toast.success('Permission updated successfully!');
        
        // Update local state
        const updatedPermissions = [...permissions];
        updatedPermissions[pendingSaveData.index] = { ...editingPermission };
        setPermissions(updatedPermissions);
        setFieldValue('permissions', updatedPermissions);
        
        // Reset editing state
        setEditingRowIndex(-1);
        setEditingPermission(null);
        setShowConfirmDialog(false);
        setPendingSaveData(null);
        
        // Refetch to ensure data consistency
        refetch();
      },
      onError: (error) => {
        toast.error('Failed to update permission. Please try again.');
        console.error('Update permission error:', error);
      }
    });
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
    setPendingSaveData(null);
  };

  const handleCancelRowEdit = () => {
    setEditingRowIndex(-1);
    setEditingPermission(null);
  };

  const handleDeletePermission = (index) => {
    const permission = permissions[index];
    setPendingDeleteData({ permission, index });
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteData) return;

    const deleteData = {
      AppId: values.AppId,
      PermissionId: parseInt(pendingDeleteData.permission.id)
    };

    deletePermission(deleteData, {
      onSuccess: () => {
        toast.success('Permission deleted successfully!');
        
        // Remove from local state
        const updatedPermissions = permissions.filter((_, i) => i !== pendingDeleteData.index);
        setPermissions(updatedPermissions);
        setFieldValue('permissions', updatedPermissions);
        
        setShowDeleteDialog(false);
        setPendingDeleteData(null);
        
        // Refetch to ensure data consistency
        refetch();
      },
      onError: (error) => {
        toast.error('Failed to delete permission. Please try again.');
        console.error('Delete permission error:', error);
      }
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setPendingDeleteData(null);
  };

  const handleSyncPermission = (index) => {
    const permission = permissions[index];
    // Check if permission has default roles and is not parent
    if (!permission.defaultRoleIds || permission.defaultRoleIds.length === 0 || permission.isParent) {
      return; // Should not reach here as button should be disabled
    }
    setPendingSyncData({ permission, index });
    setShowSyncDialog(true);
  };

  const handleConfirmSync = () => {
    if (!pendingSyncData) return;

    const syncData = {
      AppId: values.AppId,
      PermissionId: parseInt(pendingSyncData.permission.id)
    };

    // Add OverwriteAll field if checkbox is checked
    if (overwriteAll) {
      syncData.OverwriteAll = true;
    }

    syncPermission(syncData, {
      onSuccess: () => {
        toast.success('Permission synced successfully!');
        setShowSyncDialog(false);
        setPendingSyncData(null);
        setOverwriteAll(false); // Reset checkbox state
        
        // Refetch to ensure data consistency
        refetch();
      },
      onError: (error) => {
        toast.error('Failed to sync permission. Please try again.');
        console.error('Sync permission error:', error);
      }
    });
  };

  const handleCancelSync = () => {
    setShowSyncDialog(false);
    setPendingSyncData(null);
    setOverwriteAll(false); // Reset checkbox state
  };

  const handleSavePermission = () => {
    if (!newPermission.permissionName.trim()) return;

    // Find parent permission ID if parent permission is selected
    let parentPermissionId = null;
    if (!newPermission.isParent && newPermission.parentPermission) {
      const parentPerm = permissions.find(p => p.permissionName === newPermission.parentPermission);
      parentPermissionId = parentPerm ? parseInt(parentPerm.id) : null;
    }

    const addData = {
      AppId: values.AppId,
      PermissionName: newPermission.permissionName,
      PermissionId: parseInt(newPermission.id),
      IsParent: newPermission.isParent,
      ParentPermissionId: parentPermissionId,
      DefaultRoleTypeIds: newPermission.defaultRoleIds || []
    };

    addPermission(addData, {
      onSuccess: () => {
        toast.success('Permission added successfully!');
        
        // Add to local state
        const newPermissionData = {
          ...newPermission,
          parentPermissionId: parentPermissionId
        };
        const updatedPermissions = [newPermissionData, ...permissions];
        setPermissions(updatedPermissions);
        setFieldValue('permissions', updatedPermissions);
        
        handleCancelForm();
        
        // Refetch to ensure data consistency
        refetch();
      },
      onError: (error) => {
        toast.error('Failed to add permission. Please try again.');
        console.error('Add permission error:', error);
      }
    });
  };

  const handleCancelForm = () => {
    setIsAddingNew(false);
    setNewPermission({
      id: '',
      permissionName: '',
      isParent: false,
      parentPermission: '',
      defaultRole: '',
      defaultRoleIds: [],
      sync: 'O',
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset any pending operations when switching tabs
    setIsAddingNew(false);
    setEditingRowIndex(-1);
    setEditingPermission(null);
  };

  const getSyncChipProps = (sync, hasDefaultRoles = true) => {
    const baseProps = {
      variant: 'outlined',
      sx: { backgroundColor: 'transparent' }
    };
    
    if (!hasDefaultRoles) {
      return {
        ...baseProps,
        label: 'Sync',
        color: 'default',
        sx: { opacity: 0.5, backgroundColor: 'transparent' }
      };
    }
    
    switch (sync) {
      case 'O':
        return { 
          label: <RefreshIcon fontSize="small" />, 
          color: 'primary',
          variant: 'filled'
        };
      case '-':
        return { 
          label: '-', 
          color: 'default',
          variant: 'outlined'
        };
      default:
        return { 
          label: <RefreshIcon fontSize="small" />, 
          color: 'default',
          variant: 'outlined'
        };
    }
  };

  return (
    <AppGridContainer>
      <Grid item xs={12}>
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 4,
          mb: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background decoration */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            opacity: 0.7
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            opacity: 0.5
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                mr: 2, 
                p: 1.5, 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center'
              }}>
                🔐
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'white' }}>
                Permission Settings
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
              Take control of your app's security by defining granular permissions. Create a robust access control system that scales with your application and ensures users only see what they're authorized to access.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 280 }}>
                <Box sx={{ 
                  mt: 0.5,
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: '#10b981',
                  boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)'
                }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Developer-Friendly IDs
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.4 }}>
                    Assign unique numerical IDs that developers can use directly in code for permission checks
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 280 }}>
                <Box sx={{ 
                  mt: 0.5,
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)'
                }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Smart Organization
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.4 }}>
                    Group related permissions under parent categories for intuitive navigation and management
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 280 }}>
                <Box sx={{ 
                  mt: 0.5,
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: '#8b5cf6',
                  boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.2)'
                }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Role-Based Defaults
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.4 }}>
                    Automatically assign permissions to users based on their roles for streamlined onboarding
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Permission Type Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
          >
            <Tab label="Back Office" value="BackOffice" />
            <Tab label="Customer Portal" value="CustomerPortal" />
          </Tabs>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog 
          open={showConfirmDialog} 
          onClose={handleCancelSave}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Permission Update</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to update this permission? This action will modify the permission settings and may affect user access.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSave} color="inherit">
              Cancel
            </Button>
            <LoadingButton 
              onClick={handleConfirmSave} 
              loading={isUpdating}
              variant="contained"
            >
              Confirm Update
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onClose={handleCancelDelete}>
          <DialogTitle>🗑️ Delete Permission</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to permanently delete the permission <strong>"{pendingDeleteData?.permission?.permissionName}"</strong>.
              <br /><br />
              <strong>⚠️ Critical Warning:</strong>
              <ul style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '16px', color: '#d32f2f' }}>
                <li><strong>This action cannot be undone!</strong></li>
                <li>The permission will be permanently removed from the system</li>
                <li>All users will immediately lose access to this permission</li>
                <li>Any features protected by this permission will become inaccessible</li>
              </ul>
              
              <strong>📋 What will be affected:</strong>
              <ul style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '16px' }}>
                <li>All current subscribers using this permission</li>
                <li>User access to features requiring this permission</li>
                <li>Role configurations that include this permission</li>
                <li>Application functionality dependent on this permission</li>
              </ul>
              
              <strong style={{ color: '#d32f2f' }}>This may cause immediate service disruption for your users.</strong>
              <br /><br />
              Are you absolutely certain you want to delete this permission?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="inherit">
              Cancel
            </Button>
            <LoadingButton 
              onClick={handleConfirmDelete} 
              loading={isDeletingPermission}
              variant="contained"
              color="error"
            >
              Yes, Delete Permission
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Sync Confirmation Dialog */}
        <Dialog open={showSyncDialog} onClose={handleCancelSync}>
          <DialogTitle>🔄 Sync Permission Data</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to sync the permission <strong>"{pendingSyncData?.permission?.permissionName}"</strong> across all subscribers.
              <br /><br />
              <strong>📋 What this action will do:</strong>
              <ul style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '16px' }}>
                <li>Apply data correction for this permission to all app subscribers</li>
                <li>Grant this permission to users whose roles match the configured default roles</li>
                <li>Only affect users with role-based permission inheritance enabled</li>
                <li>Skip users with custom permission configurations</li>
              </ul>
              
              <strong>⚠️ Important Notes:</strong>
              <ul style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '16px' }}>
                <li>This action will immediately update user permissions</li>
                <li>Users may gain or lose access to features based on their current roles</li>
                <li>The sync process cannot be undone once completed</li>
              </ul>
              
              Do you want to proceed with syncing this permission?
            </DialogContentText>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={overwriteAll}
                    onChange={(e) => setOverwriteAll(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.800' }}>
                      Force overwrite all custom settings
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'warning.700', display: 'block', mt: 0.5 }}>
                      ⚠️ <strong>Warning:</strong> If checked, this will overwrite all custom permission settings 
                      that customers may have configured for this permission. This action affects all subscribers and cannot be undone.
                      You don't need to be cautious if this is a new permission that hasn't been deployed to customers yet.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSync} color="inherit">
              Cancel
            </Button>
            <LoadingButton 
              onClick={handleConfirmSync} 
              loading={isSyncingPermission}
              variant="contained"
              color="primary"
            >
              Yes, Sync Permission
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ mt: 4 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={400} />
          </Box>
        )}

        {/* Error State */}
        {apiError && (
          <Alert severity="error" sx={{ mt: 4 }}>
            Failed to load permissions: {apiError.message}
          </Alert>
        )}

        {/* Main Content - only show when not loading */}
        {!isLoading && (
          <>
            {/* Add Button */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                mb: 3,
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddPermission}
                sx={{ whiteSpace: 'nowrap' }}
                disabled={editingRowIndex !== -1 || isAddingNew}
              >
                Add
              </Button>
            </Box>

        {/* Permissions Table */}
        <TableContainer component={Paper} sx={{ border: '1px solid #E0E0E0' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F5F5F5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>ID</span>
                    <Tooltip title="Unique identifier for this permission. Developers use this ID in code to check if users have this specific permission (e.g., if(user.hasPermission(123)) { ... })" arrow>
                      <InfoOutlined sx={{ fontSize: 16, color: 'grey.500', cursor: 'pointer', display: 'flex', alignItems: 'center' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Permission</span>
                    <Tooltip title="Human-readable name that will be displayed to end users in the application interface" arrow>
                      <InfoOutlined sx={{ fontSize: 16, color: 'grey.500', cursor: 'pointer', display: 'flex', alignItems: 'center' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Group?</span>
                    <Tooltip title="When enabled, this permission acts as a visual grouping container for organizing other permissions. Parent permissions are not actual permissions - they're only used for UI organization" arrow>
                      <InfoOutlined sx={{ fontSize: 16, color: 'grey.500', cursor: 'pointer', display: 'flex', alignItems: 'center' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Parent Group</span>
                    <Tooltip title="Select which parent group this permission belongs to. This helps organize permissions into logical categories for better user experience" arrow>
                      <InfoOutlined sx={{ fontSize: 16, color: 'grey.500', cursor: 'pointer', display: 'flex', alignItems: 'center' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Default Roles</span>
                    <Tooltip title="Users with these roles will automatically receive this permission when the permission system is initialized. You can select multiple roles that should have this permission by default" arrow>
                      <InfoOutlined sx={{ fontSize: 16, color: 'grey.500', cursor: 'pointer', display: 'flex', alignItems: 'center' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Inline Add Row */}
              {isAddingNew && (
                <TableRow sx={{ 
                  border: '2px solid #1976d2', 
                  backgroundColor: '#f3f8ff',
                  '& .MuiTableCell-root': {
                    borderColor: '#1976d2'
                  }
                }}>
                  <TableCell>
                    <TextField
                      value={newPermission.id}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        if (/^\d*$/.test(value)) {
                          setNewPermission({ ...newPermission, id: value });
                        }
                      }}
                      size="small"
                      sx={{ width: '80px' }}
                      placeholder="ID"
                      type="text"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={newPermission.permissionName}
                      onChange={(e) => setNewPermission({ ...newPermission, permissionName: e.target.value })}
                      size="small"
                      fullWidth
                      required
                      placeholder="Permission Name"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Checkbox
                        checked={newPermission.isParent}
                        onChange={(e) => setNewPermission({ 
                          ...newPermission, 
                          isParent: e.target.checked,
                          parentPermission: e.target.checked ? 'Dropdown' : '',
                          defaultRole: e.target.checked ? 'Disabled' : '',
                          defaultRoleIds: e.target.checked ? [] : [],
                        })}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={newPermission.parentPermission}
                        onChange={(e) => setNewPermission({ ...newPermission, parentPermission: e.target.value })}
                        displayEmpty
                        disabled={newPermission.isParent}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {newPermission.isParent ? (
                          <MenuItem value="Dropdown">Dropdown</MenuItem>
                        ) : (
                          parentPermissions.length > 0 ? (
                            parentPermissions.map((parent) => (
                              <MenuItem key={parent.id} value={parent.permissionName}>
                                {parent.permissionName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              No parent available
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        value={newPermission.defaultRoleIds || []}
                        onChange={(e) => {
                          const selectedRoleIds = e.target.value;
                          const selectedRoleNames = selectedRoleIds
                            .map(id => currentRoleTypeNames[id])
                            .filter(Boolean)
                            .join(', ');
                          
                          setNewPermission({
                            ...newPermission,
                            defaultRoleIds: selectedRoleIds,
                            defaultRole: selectedRoleNames || 'Disabled'
                          });
                        }}
                        disabled={newPermission.isParent}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((roleId) => (
                              <Chip
                                key={roleId}
                                label={currentRoleTypeNames[roleId]}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                              width: 250,
                            },
                          },
                        }}
                      >
                        {roleOptions.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            <Checkbox 
                              checked={(newPermission.defaultRoleIds || []).indexOf(role.id) > -1}
                              size="small"
                            />
                            <ListItemText primary={role.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Save" arrow>
                        <IconButton
                          onClick={handleSavePermission}
                          color="primary"
                          size="small"
                          disabled={!newPermission.permissionName.trim() || !newPermission.id.trim() || isAddingPermission}
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel" arrow>
                        <IconButton
                          onClick={handleCancelForm}
                          color="error"
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {/* Existing permissions */}
              {permissions.map((permission, index) => {
                const isEditing = editingRowIndex === index;
                const currentPermission = isEditing ? editingPermission : permission;
                
                return (
                <TableRow key={permission.id} hover sx={isEditing ? { 
                  border: '2px solid #1976d2', 
                  backgroundColor: '#f3f8ff',
                  '& .MuiTableCell-root': {
                    borderColor: '#1976d2'
                  }
                } : {}}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={currentPermission.id}
                        onChange={(e) => setEditingPermission({ ...editingPermission, id: e.target.value })}
                        size="small"
                        sx={{ width: '80px' }}
                        disabled
                      />
                    ) : (
                      permission.id
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={currentPermission.permissionName}
                        onChange={(e) => setEditingPermission({ ...editingPermission, permissionName: e.target.value })}
                        size="small"
                        fullWidth
                        required
                      />
                    ) : (
                      permission.permissionName
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Chip 
                        icon={currentPermission.isParent ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                        label={currentPermission.isParent ? 'Yes' : 'No'} 
                        color={currentPermission.isParent ? 'success' : 'default'}
                        size="small"
                        variant={currentPermission.isParent ? 'filled' : 'outlined'}
                      />
                    ) : (
                      <Chip 
                        icon={permission.isParent ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                        label={permission.isParent ? 'Yes' : 'No'} 
                        color={permission.isParent ? 'success' : 'default'}
                        size="small"
                        variant={permission.isParent ? 'filled' : 'outlined'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      currentPermission.isParent ? (
                        <Chip label="Disabled" color="default" size="small" />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={currentPermission.parentPermission || ''}
                            onChange={(e) => setEditingPermission({
                              ...editingPermission,
                              parentPermission: e.target.value
                            })}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {parentPermissions.length > 0 ? (
                              parentPermissions.map((parent) => (
                                <MenuItem key={parent.id} value={parent.permissionName}>
                                  {parent.permissionName}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                No parent available
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      )
                    ) : (
                      permission.isParent ? (
                        <Chip label="Disabled" color="default" size="small" />
                      ) : (
                        permission.parentPermission || 'None'
                      )
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      currentPermission.isParent ? (
                        <Chip label="Disabled" color="default" size="small" />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            multiple
                            value={currentPermission.defaultRoleIds || []}
                            onChange={(e) => {
                              const selectedRoleIds = e.target.value;
                              const selectedRoleNames = selectedRoleIds
                                .map(id => currentRoleTypeNames[id])
                                .filter(Boolean)
                                .join(', ');
                              
                              setEditingPermission({
                                ...editingPermission,
                                defaultRoleIds: selectedRoleIds,
                                defaultRole: selectedRoleNames || 'Disabled'
                              });
                            }}
                            input={<OutlinedInput />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((roleId) => (
                                  <Chip
                                    key={roleId}
                                    label={currentRoleTypeNames[roleId]}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 224,
                                  width: 250,
                                },
                              },
                            }}
                          >
                            {roleOptions.map((role) => (
                              <MenuItem key={role.id} value={role.id}>
                                <Checkbox 
                                  checked={(currentPermission.defaultRoleIds || []).indexOf(role.id) > -1}
                                  size="small"
                                />
                                <ListItemText primary={role.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )
                    ) : (
                      permission.isParent ? (
                        <Chip label="Disabled" color="default" size="small" />
                      ) : (
                        permission.defaultRole && permission.defaultRole.trim() && permission.defaultRole !== 'Disabled' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {permission.defaultRole.split(', ').map((role, idx) => (
                              <Chip 
                                key={idx}
                                label={role} 
                                color="primary" 
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : ''
                      )
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Save" arrow>
                          <IconButton 
                            size="small" 
                            onClick={() => handleSaveRowEdit(index)}
                            color="primary"
                            disabled={!currentPermission.permissionName?.trim()}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel" arrow>
                          <IconButton 
                            size="small" 
                            onClick={handleCancelRowEdit}
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Sync" arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(!permission.defaultRoleIds || permission.defaultRoleIds.length === 0 || permission.isParent || editingRowIndex !== -1 || isAddingNew) ? undefined : () => handleSyncPermission(index)}
                              disabled={!permission.defaultRoleIds || permission.defaultRoleIds.length === 0 || permission.isParent || editingRowIndex !== -1 || isAddingNew}
                              color="primary"
                              sx={{ 
                                opacity: (!permission.defaultRoleIds || permission.defaultRoleIds.length === 0 || permission.isParent) ? 0.5 : 1,
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditPermission(index)}
                            color="primary"
                            disabled={editingRowIndex !== -1 && editingRowIndex !== index || isAddingNew}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeletePermission(index)}
                            color="error"
                            disabled={editingRowIndex !== -1 || isAddingNew}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
              {permissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No permissions configured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
        )}
      </Grid>
    </AppGridContainer>
  );
};

PermissionConfigurationStep.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func,
};

export default React.memo(PermissionConfigurationStep);
